import type { MonitoringInvestigationContext } from "../../../shared/chatInvestigation";
import { AskMonitoringChat } from "./AskMonitoringChat";
import { styled } from "goober";
import {
  getAIProvider,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { isManagedAiConnectorId } from "../../../shared/aiProviders/managedAiConnector";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { GhostPill } from "../GhostPill";
import { ProviderLogo } from "../ProviderLogo";
import { SegmentedControl } from "../SegmentedControl";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  AiBreakdownTable,
  type BreakdownColumn,
  type BreakdownRow,
  GhostBadge,
} from "./AiBreakdownTable";
import { useMonitoringInteraction } from "./analytics";
import {
  type AiUsageRecord,
  computeModelUsageRows,
  formatCacheRatio,
  formatEstimatedCostUsd,
  formatTokenCount,
} from "./aiUsage";
import {
  type AiUsageTotals,
  aiUsageBucketId,
  type CostActivity,
  groupAiUsage,
  PERSONAL_CHAT_BUCKET,
  UNATTRIBUTED_BUCKET,
} from "./aiUsageBreakdown";
import { NameBody, NameCell } from "./monitoringList";
import { spaceColorMap } from "./monitoringPanelLayout";
import { useMonitoringSpaces } from "./useMonitoringWorkflows";
import type { WindowOption } from "./monitoringWindow";
import { Panel, PanelHeader } from "./workflowsLayout";

const VIEWS = ["models", "workflows", "spaces"] as const;
type View = (typeof VIEWS)[number];

const VIEW_LABELS: Record<View, string> = {
  models: "Models",
  workflows: "Workflows",
  spaces: "Spaces",
};
const VIEW_BY_LABEL = new Map(
  VIEWS.map((view) => [VIEW_LABELS[view], view] as const)
);

const HeaderRow = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Swatch = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
`;

type CostKey = CostActivity | "cost";

const COST_COLUMNS: readonly BreakdownColumn<CostKey>[] = [
  { key: "build", label: "Build", format: formatEstimatedCostUsd },
  { key: "run", label: "Run", format: formatEstimatedCostUsd },
  { key: "monitor", label: "Monitor", format: formatEstimatedCostUsd },
  { key: "other", label: "Other", format: formatEstimatedCostUsd },
  { key: "cost", label: "Est. cost", format: formatEstimatedCostUsd },
];

function costValues(totals: AiUsageTotals): Record<CostKey, number | null> {
  return { ...totals.activityCost, cost: totals.cost };
}

type UsageProps = {
  records: readonly AiUsageRecord[];
  prices: ReadonlyMap<string, number>;
};

type Props = UsageProps & {
  investigation: MonitoringInvestigationContext;
  loading: boolean;
  workflowRecords: readonly AiUsageRecord[];
  workflowsLoading: boolean;
  connectorLabels: ReadonlyMap<string, string>;
  lookback: WindowOption;
};

export function AiBreakdownPanel({
  records,
  workflowRecords,
  workflowsLoading,
  prices,
  connectorLabels,
  lookback,
  investigation,
  loading,
}: Props) {
  const recordInteraction = useMonitoringInteraction();
  const { view } = useCurrentUrl(routes.monitoring.ai);
  const active: View = VIEWS.includes(view as View) ? (view as View) : "models";
  const descriptions: Record<View, string> = {
    models: `Tokens, cache hit rate, and estimated cost per model ${lookback.phrase}.`,
    workflows: `Top 100 workflows by usage across all providers ${lookback.phrase}, with estimated cost split by activity. Monitor is autofix and autotune; Other is usage outside a workflow’s lifecycle, such as personal chat.`,
    spaces: `Estimated cost per space ${lookback.phrase}. Personal spaces are listed by owner. Personal chat has no workflow, so it counts as Other.`,
  };

  return (
    <Panel style={{ minHeight: 0 }}>
      <HeaderRow>
        <PanelHeader>
          <Text variant="text-small-md">Usage breakdown</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {descriptions[active]}
          </Text>
        </PanelHeader>
        <SegmentedControl
          options={VIEWS.map((view) => VIEW_LABELS[view])}
          value={VIEW_LABELS[active]}
          tabWidth={64}
          onChange={(label) => {
            const next = VIEW_BY_LABEL.get(label) ?? "models";
            recordInteraction("ai.breakdown_view_change", { value: next });
            // Sort columns differ per view.
            navigate(
              routes.monitoring.ai,
              { view: next, sort: "", dir: "" },
              { replace: true }
            );
          }}
        />
      </HeaderRow>
      {active === "models" ? (
        <ModelsView
          records={records}
          prices={prices}
          connectorLabels={connectorLabels}
          emptyMessage={`No AI usage recorded ${lookback.phrase}.`}
        />
      ) : active === "workflows" ? (
        <WorkflowsView
          records={workflowRecords}
          prices={prices}
          loading={workflowsLoading}
          emptyMessage={`No workflow-attributed AI usage ${lookback.phrase}.`}
        />
      ) : (
        <SpacesView
          records={records}
          prices={prices}
          emptyMessage={`No AI usage recorded ${lookback.phrase}.`}
        />
      )}
      <AskMonitoringChat
        disabled={loading || (active === "workflows" && workflowsLoading)}
        surface="ai-usage"
        context={{
          ...investigation,
          filters: { ...investigation.filters, view: active },
        }}
        snapshot={descriptions[active]}
      />
    </Panel>
  );
}

const MODEL_COLUMNS: readonly BreakdownColumn<"tokens" | "cache" | "cost">[] = [
  { key: "tokens", label: "Tokens", format: (v) => formatTokenCount(v ?? 0) },
  { key: "cache", label: "Cache", format: formatCacheRatio },
  { key: "cost", label: "Est. cost", format: formatEstimatedCostUsd },
];

function ModelsView({
  records,
  prices,
  connectorLabels,
  emptyMessage,
}: UsageProps & {
  connectorLabels: ReadonlyMap<string, string>;
  emptyMessage: string;
}) {
  const makeUrl = useMakeUrl();
  const rows = computeModelUsageRows(records, prices).map((row) => {
    const providerId = resolveAIProviderId({ provider: row.provider });
    // Managed usage has a synthetic connector id with no connector modal.
    const managed = isManagedAiConnectorId(row.connectorId ?? "");
    return {
      key: `${row.connectorId ?? ""}\0${row.model}`,
      targetId: row.connectorId ?? undefined,
      link:
        !managed && row.connectorId
          ? makeUrl(routes.modal.connector, {
              id: row.connectorId,
              tab: "usage",
            })
          : makeUrl(routes.modal.aiProvider, {
              id: row.aiProviderId ?? MANAGED_AI_PROVIDER_ID,
            }),
      name: (
        <NameCell>
          <ConnectorLogoContainer
            size={32}
            borderRadius={8}
            outline={false}
            hoverBackground={false}
          >
            <ProviderLogo provider={providerId} size={16} />
          </ConnectorLogoContainer>
          <NameBody>
            <Text variant="text-regular-md" ellipsis>
              {row.model}
            </Text>
            <Text variant="text-small" color="var(--text-secondary)" ellipsis>
              {connectorLabels.get(row.connectorId ?? "") ??
                getAIProvider(providerId).label}
            </Text>
          </NameBody>
        </NameCell>
      ),
      values: {
        tokens: row.totalTokens,
        cache:
          row.inputTokens === 0 ? null : row.cacheReadTokens / row.inputTokens,
        cost: row.estimatedCostUsd,
      },
    };
  });
  return (
    <AiBreakdownTable
      nameHeader="Model"
      columns={MODEL_COLUMNS}
      rows={rows}
      emptyMessage={emptyMessage}
      sortEvent="ai.model_table_sort"
      linkEvent="ai.model_link_click"
    />
  );
}

function WorkflowsView({
  records,
  prices,
  loading,
  emptyMessage,
}: UsageProps & { loading: boolean; emptyMessage: string }) {
  const makeUrl = useMakeUrl();
  const [workflows, workflowsQuery] = useQuery(queries.allWorkflows());
  const { spaceById } = useMonitoringSpaces();
  const displayName = useSpaceDisplayName();
  const workflowById = new Map(
    (workflows ?? [])
      .filter((workflow) => !workflow.deletedAt)
      .map((workflow) => [workflow.id, workflow])
  );
  const rows: BreakdownRow<CostKey>[] = [
    ...groupAiUsage(records, prices, (record) => record.workflowId),
  ].map(([workflowId, totals]) => {
    const workflow = workflowById.get(workflowId);
    const space = workflow ? spaceById.get(workflow.spaceId) : undefined;
    // Unsynced names stay placeholders so rows never read “Deleted workflow” early.
    const pending = !workflow && workflowsQuery.type !== "complete";
    return {
      key: workflowId,
      targetId: workflowId,
      link: workflow
        ? makeUrl(routes.space.workflow, {
            spaceId: workflow.spaceId,
            workflowId,
          })
        : undefined,
      name: (
        <NameCell>
          {workflow ? (
            <WorkflowBadge
              workflow={{
                id: workflow.id,
                spaceId: workflow.spaceId,
                badgeSeed: workflow.badgeSeed,
                pausedAt: workflow.pausedAt,
                // Anything with usage in the window is live, so render the
                // full-color (non-blueprint) badge.
                branches: [{ name: "main" }],
              }}
              hover={false}
            />
          ) : (
            <GhostBadge />
          )}
          <NameBody>
            {pending ? (
              <GhostPill style={{ width: 128 }} />
            ) : (
              <Text variant="text-regular-md" ellipsis>
                {workflow?.name ?? "Deleted workflow"}
              </Text>
            )}
            {space && (
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {displayName(space)}
              </Text>
            )}
          </NameBody>
        </NameCell>
      ),
      values: costValues(totals),
    };
  });
  return (
    <AiBreakdownTable
      nameHeader="Workflow"
      columns={COST_COLUMNS}
      rows={rows}
      emptyMessage={emptyMessage}
      loading={loading}
      sortEvent="ai.workflow_table_sort"
      linkEvent="ai.workflow_link_click"
    />
  );
}

function SpacesView({
  records,
  prices,
  emptyMessage,
}: UsageProps & { emptyMessage: string }) {
  const makeUrl = useMakeUrl();
  const { spaces, spaceById } = useMonitoringSpaces();
  const displayName = useSpaceDisplayName();
  const colorMap = spaceColorMap(spaces);

  const rows: BreakdownRow<CostKey>[] = [
    ...groupAiUsage(records, prices, aiUsageBucketId),
  ].map(([id, totals]) => {
    const space = spaceById.get(id);
    const label =
      id === PERSONAL_CHAT_BUCKET
        ? "Personal chat"
        : id === UNATTRIBUTED_BUCKET
          ? "Unattributed"
          : space
            ? displayName(space)
            : "Unknown space";
    const color =
      id === PERSONAL_CHAT_BUCKET
        ? "var(--gray-600)"
        : id === UNATTRIBUTED_BUCKET
          ? "var(--gray-400)"
          : (colorMap.get(id) ?? "var(--alpha8)");
    return {
      key: id,
      targetId: space ? id : undefined,
      link: space ? makeUrl(routes.space, { spaceId: id }) : undefined,
      name: (
        <NameCell>
          <Swatch style={{ background: color }} />
          <NameBody>
            <Text variant="text-regular-md" ellipsis>
              {label}
            </Text>
          </NameBody>
        </NameCell>
      ),
      values: costValues(totals),
    };
  });
  return (
    <AiBreakdownTable
      nameHeader="Space"
      columns={COST_COLUMNS}
      rows={rows}
      emptyMessage={emptyMessage}
      sortEvent="ai.space_table_sort"
      linkEvent="ai.space_link_click"
    />
  );
}
